import { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  InputAdornment,
  OutlinedInput,
  FormControl,
  Button,
  Link,
} from "@mui/material";
import SettingTitle from "../../component/SettingTitle";
import { styles } from "./stylesConfirmPassword";
import EyeSvg from "@/assets/svg/EyeSvg";
import theme from "@/theme";
import { books } from "@/assets/books";
import { useNavigate, useLocation } from "react-router-dom";
import { usePasswordConfirm } from "@/context/PasswordConfirmContext";
import { useValidatePasswordMutation } from "@/store/api/authApi";

const passwordRegex = /^(?=.*\d)(?=.*[^\w\s]).{8,}$/;

const ConfirmPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { confirmPassword } = usePasswordConfirm();
  const [validatePassword] = useValidatePasswordMutation();

  const isValid = password.length > 0 && passwordRegex.test(password);

  const from = location.state?.from || "/settings/account/information";

  const handleSave = async () => {
    if (!isValid || isLoading) return;

    setServerError(null);
    setIsLoading(true);

    try {
      const res = await validatePassword({ password }).unwrap();
      if (res.valid) {
        confirmPassword();
        navigate(from, { replace: true });
      } else {
        setServerError("The password is incorrect. Please try another one");
      }
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || "Something went wrong";
      setServerError(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setServerError(null);
    setPassword(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isValid) {
      handleSave();
    }
  };

  return (
    <>
      <SettingTitle title="Account information" submit={handleSave} back isActive={isValid} />
      <Box sx={styles.formRoot} component="form" autoComplete="off">
        <Box sx={styles.sectionHeader}>
          <Typography
            color={theme.palette.custom.blueGray900}
            sx={{
              fontFamily: "Inter",
              fontWeight: 600,
              fontSize: "16px",
              lineHeight: "24px",
            }}
          >
            Confirm your password
          </Typography>
          <Typography
            color={theme.palette.custom.blueGray600}
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "12px",
              lineHeight: "16px",
            }}
          >
            Please enter your password in order to get this.
          </Typography>
        </Box>

        <Box sx={styles.inputsBlock}>
          <input type="password" autoComplete="new-password" style={{ display: "none" }} />
          <Box sx={styles.inputGroup}>
            <Typography sx={styles.label}>Password</Typography>
            <Box sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
              <FormControl fullWidth error={!!serverError}>
                <OutlinedInput
                  value={password}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter your password"
                  type={showPassword ? "text" : "password"}
                  error={!!serverError}
                  sx={{
                    ...styles.input,
                    "& input": {
                      WebkitTextSecurity: !showPassword ? "*" : "none",
                    },
                    ...(serverError && {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E62A3B",
                      },
                    }),
                  }}
                  autoComplete="off"
                  name="confirmPwd"
                  id="confirmPwd"
                  endAdornment={
                    <InputAdornment position="end" sx={styles.endAdornment}>
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        aria-label="toggle password visibility"
                        sx={styles.iconButton}
                      >
                        <EyeSvg crossed={!showPassword} color={password ? "#383C40" : "#C6CACE"} />
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {serverError && (
                  <Typography
                    sx={{
                      color: "#E62A3B",
                      fontSize: "12px",
                      fontWeight: 400,
                      lineHeight: "16px",
                      mt: 0.5,
                    }}
                  >
                    {serverError}
                  </Typography>
                )}
              </FormControl>
              <Button
                onClick={handleSave}
                variant="contained"
                sx={{
                  bgcolor: isValid ? theme.palette.custom.mainBlue : "#8BC5F4",
                  borderRadius: "28px",
                  minWidth: "110px",
                  height: "50px",
                  color: "#fff",
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 500,
                  lineHeight: "22px",
                  padding: "14px 24px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: isValid ? theme.palette.custom.mainBlue : "#8BC5F4",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#8BC5F4",
                    color: "#FFFFFF",
                    opacity: 1,
                  },
                }}
                disabled={!isValid}
              >
                Confirm
              </Button>
            </Box>
          </Box>
          <Link href={books.forgotPassword} sx={styles.Link}>
            Forgot password?
          </Link>
        </Box>
      </Box>
    </>
  );
};

export default ConfirmPassword;
